import React, { useEffect, useMemo, useRef, useState } from "react";
import "./searchableSelect.css";

/*
 * SIP HRD searchable combobox V1.3
 * Satu field saja: awalnya tampil placeholder "Pilih Karyawan/Pilih Training".
 * Saat diklik, user langsung bisa mengetik di field yang sama dan pilihan
 * di bawah akan terfilter realtime.
 */

function textFromChildren(value) {
  if (Array.isArray(value)) {
    return value
      .map((x) => {
        if (typeof x === "string" || typeof x === "number") {
          return String(x);
        }
        return "";
      })
      .join(" ");
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return "";
}

function getOptionText(node) {
  return textFromChildren(node?.props?.children)
    .replace(/\s+/g, " ")
    .trim();
}

function getOptionValue(node) {
  return node?.props?.value == null ? "" : String(node.props.value);
}

export default function SearchableSelect({
  children,
  value,
  onChange,
  className,
  style,
  disabled,
  required,
  ...rest
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo(
    () =>
      React.Children.toArray(children).filter(
        (child) => React.isValidElement(child) && child.type === "option"
      ),
    [children]
  );

  const placeholderOption = useMemo(
    () => options.find((o) => !getOptionValue(o)),
    [options]
  );

  const placeholder =
    getOptionText(placeholderOption) || "Pilih data";

  const selectedValue = value == null ? "" : String(value);

  const selectedOption = useMemo(
    () => options.find((o) => getOptionValue(o) === selectedValue),
    [options, selectedValue]
  );

  const selectedText = getOptionText(selectedOption);

  useEffect(() => {
    if (!open) {
      setQuery(selectedText || "");
    }
  }, [selectedText, open]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(selectedText || "");
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [selectedText]);

  const realOptions = useMemo(
    () => options.filter((o) => Boolean(getOptionValue(o))),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();

    // Ketika baru klik field yang sudah punya value, tampilkan semua pilihan.
    if (!q || q === String(selectedText || "").toLowerCase()) {
      return realOptions;
    }

    return realOptions.filter((option) =>
      getOptionText(option).toLowerCase().includes(q)
    );
  }, [query, realOptions, selectedText]);

  useEffect(() => {
    if (activeIndex >= filteredOptions.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, filteredOptions.length]);

  const emitChange = (nextValue) => {
    const target = {
      value: nextValue,
      name: rest.name || "",
    };

    onChange?.({
      target,
      currentTarget: target,
      preventDefault() {},
      stopPropagation() {},
    });
  };

  const selectOption = (option) => {
    if (!option || disabled) return;

    const nextValue = getOptionValue(option);
    const nextText = getOptionText(option);

    emitChange(nextValue);
    setQuery(nextText);
    setOpen(false);
    setActiveIndex(0);
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) return;

    emitChange("");
    setQuery("");
    setOpen(true);
    setActiveIndex(0);

    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleFocus = (event) => {
    if (disabled) return;

    setOpen(true);
    setActiveIndex(0);

    // Jika sudah ada pilihan, ketik baru langsung menggantikan teks lama.
    requestAnimationFrame(() => event.target.select());
  };

  const handleChange = (event) => {
    setQuery(event.target.value);
    setOpen(true);
    setActiveIndex(0);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) =>
        filteredOptions.length
          ? Math.min(i + 1, filteredOptions.length - 1)
          : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      if (open && filteredOptions.length) {
        event.preventDefault();
        selectOption(filteredOptions[activeIndex] || filteredOptions[0]);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setQuery(selectedText || "");
      return;
    }
  };

  return (
    <div
      ref={rootRef}
      className={`hrd-search-combobox ${disabled ? "is-disabled" : ""}`}
      style={style}
    >
      <div className="hrd-search-combobox-control">
        <input
          {...rest}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          className={`${className || ""} hrd-search-combobox-input`}
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={handleFocus}
          onClick={() => !disabled && setOpen(true)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />

        {selectedValue || query ? (
          <button
            type="button"
            className="hrd-search-combobox-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSelection}
            aria-label="Kosongkan pilihan"
            title="Kosongkan pilihan"
          >
            ×
          </button>
        ) : (
          <span className="hrd-search-combobox-chevron" aria-hidden="true">
            ▾
          </span>
        )}
      </div>

      {open && !disabled ? (
        <div className="hrd-search-combobox-dropdown" role="listbox">
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionText = getOptionText(option);
              const active = index === activeIndex;
              const selected = optionValue === selectedValue;

              return (
                <button
                  type="button"
                  key={`${optionValue}-${index}`}
                  className={`hrd-search-combobox-option ${active ? "is-active" : ""} ${selected ? "is-selected" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectOption(option)}
                  role="option"
                  aria-selected={selected}
                >
                  {optionText}
                </button>
              );
            })
          ) : (
            <div className="hrd-search-combobox-empty">
              Tidak ada pilihan yang cocok
            </div>
          )}
        </div>
      ) : null}

      {/* Penjaga nilai untuk validasi/logika form existing. */}
      <input
        type="hidden"
        name={rest.name || undefined}
        value={selectedValue}
        data-required={required ? "1" : "0"}
        readOnly
      />
    </div>
  );
}
